import {
  CadModelObject,
  SheetObject,
  useCached3DObjectIfExists,
  useCached3DObjectsIfExists,
  usePreload3DObjects,
  type PointCloudObject,
} from "@/object-cache";
import { isPointCloudObject, UnknownObject } from "@/object-cache-type-guard";
import { Measurement } from "@/store/measurement-tool-slice";
import { isIElementOrthomosaic } from "@/types/ielement-extra-types";
import { SceneFilter } from "@/types/scene-filter";
import { assert } from "@faro-lotv/foundation";
import {
  isIElementGaussianSplats,
  isIElementGenericPointCloudStream,
  isIElementGenericStream,
  isIElementImg2d,
  isIElementImg360,
  isIElementModel3dStream,
  type IElementGenericStream,
} from "@faro-lotv/ielement-types";
import { useThree } from "@react-three/fiber";
import { RefObject, useLayoutEffect, useMemo } from "react";
import { OrthographicCamera } from "three";
import { useIsCurrentAreaLoading } from "../../mode-data-context";
import { CurrentScene } from "../../mode-selectors";
import { CurrentAreaData } from "../../select-current-area-data";
import {
  FlyWalkSceneActiveElement,
  isFlyWalkSceneActiveElement,
} from "../flywalk-types";
import { InitialAnimationInfo } from "./flywalk-animation";
import { useFlyWalkSceneElements } from "./flywalk-scene-selectors";
import { useActivateZoomLens } from "./use-activate-zoom-lens";
import {
  AnnotationDispatch,
  useDispatchAnnotations,
} from "./use-dispatch-annotations";
import { useDispatchMeasurements } from "./use-dispatch-measurements";
import {
  FlyWalkAnimationData,
  useFlyWalkAnimation,
} from "./use-flywalk-animation";
import { useInitialAnimationParams } from "./use-initial-animation-params";
import { useSuspend } from "./use-suspend";

export enum ImageType {
  Pano = "Pano",
  Photo2D = "Photo2D",
  Orthomosaic = "Orthomosaic",
  NoImage = "NoImage",
}

export type DataDispatchResult = {
  /** Main ielement for the flywalk scene */
  main: FlyWalkSceneActiveElement | null;
  /** Main renderable object for the flywalk scene */
  mainObject: UnknownObject | null;
  /** Overlay ielement in flywalk mode */
  overlay: IElementGenericStream | null;
  /** Overlay object for the flywalk scene */
  overlayObject: PointCloudObject | CadModelObject | null;
  /** Sheet objects for the flywalk scene */
  sheetObjects: SheetObject[];
  /** Annotations to be rendered */
  annotations: AnnotationDispatch;
  /** Measurements to be rendered */
  measurements: Measurement[];
  /** If main is an image, which image type it is */
  imageType: ImageType;
};

/**
 * This hook computes the project data that should be loaded and dispatched to be rendered into flywalk mode.
 * It wraps selectors to use the current area data and the current scene data, plus some mode-specific computation
 * that depends on the current flywalk scene filter. It also handles preloading of the 3D objects that are needed for the current scene.
 *
 * @param flyWalkSceneFilter The current scene filter for flywalk mode
 * @param currentScene The current scene data
 * @param currentArea The current area data
 * @returns Data from the current scene ready to be rendered in flywalk mode
 */
function useFlyWalkDataDispatch(
  flyWalkSceneFilter: SceneFilter,
  currentScene: CurrentScene,
  currentArea: CurrentAreaData,
): DataDispatchResult {
  // Get data from store
  const isLoading = useIsCurrentAreaLoading();

  const camera = useThree((s) => s.camera);

  const [main, overlay] = useFlyWalkSceneElements(
    currentScene,
    currentArea,
    camera.position,
    flyWalkSceneFilter,
  );

  // Suspend while area data is loading, but not when previous data is already available
  useSuspend(!main && !currentScene.cad && isLoading);

  assert(
    !!(main && isFlyWalkSceneActiveElement(main)) || !!currentScene.cad,
    "FlyWalk Scene requires either an image as the main object, a Point Cloud, a Gaussian Splats or an active CAD",
  );

  // Memoize the array to not re-create it at every re-render and
  // mess up references to cache objects
  const elementsToPreload = useMemo(
    () => [...currentScene.activeSheets, main, currentScene.cad],
    [currentScene.activeSheets, main, currentScene.cad],
  );

  // Preload all needed data in parallel
  usePreload3DObjects(elementsToPreload, "FlyWalkDataPreload");

  /** Get the current 3D object (do not report loading failure for the IElementModel3dStream)*/
  const displayLoadingErrorCurrentElement =
    !!main && (!isIElementModel3dStream(main) || !isIElementImg2d(main));
  const mainObject = useCached3DObjectIfExists(
    main,
    "FlyWalkDataDispatchMain",
    displayLoadingErrorCurrentElement,
  );

  assert(
    !overlay ||
      isIElementModel3dStream(overlay) ||
      isIElementGenericPointCloudStream(overlay),
    "Overlay element must be a CAD model or a point cloud stream",
  );
  const displayLoadingErrorOverlayElement =
    !!overlay && !isIElementGenericStream(overlay);
  const overlayObject = useCached3DObjectIfExists(
    overlay,
    "FlyWalkDataDispatchOverlay",
    displayLoadingErrorOverlayElement,
  );

  const sheetObjects = useCached3DObjectsIfExists(
    currentScene.activeSheets,
    "FlyWalkDataDispatchMain",
  );

  const annotations = useDispatchAnnotations(
    currentScene.annotations,
    main ?? null,
  );

  const measurements = useDispatchMeasurements(main ?? null);

  const imageType = useMemo<ImageType>(() => {
    if (!main) {
      return ImageType.NoImage;
    }
    if (isIElementImg360(main)) {
      return ImageType.Pano;
    } else if (isIElementImg2d(main)) {
      return ImageType.Photo2D;
    } else if (isIElementOrthomosaic(main)) {
      return ImageType.Orthomosaic;
    }
    return ImageType.NoImage;
  }, [main]);

  return useMemo<DataDispatchResult>(
    () => ({
      main: main ?? null,
      mainObject,
      overlay: overlay ?? null,
      overlayObject,
      sheetObjects,
      annotations,
      measurements,
      imageType,
    }),
    [
      main,
      mainObject,
      overlay,
      overlayObject,
      sheetObjects,
      annotations,
      measurements,
      imageType,
    ],
  );
}

type FlyWalkDataDispatchProps = {
  /** Scene filter in flywalk mode */
  flyWalkSceneFilter: SceneFilter;
  /** Current scene data */
  currentScene: CurrentScene;
  /** Current area data */
  currentArea: CurrentAreaData;
  /** Information about the initial animation when entering flywalk mode */
  initialAnimationInfo: RefObject<InitialAnimationInfo | null>;
  /** The default ortho camera that can be used to render this scene */
  defaultOrthoCamera?: OrthographicCamera;
  /** Callback to output the data dispatch result */
  setDataDispatchResult(result: DataDispatchResult): void;
  /** Callback to output the current animation data */
  setAnimationData(result: FlyWalkAnimationData): void;
};

/** @returns A component that is responsible for the data dispatch, and can suspend */
export function FlyWalkDataDispatch({
  flyWalkSceneFilter,
  currentScene,
  currentArea,
  initialAnimationInfo,
  defaultOrthoCamera,
  setDataDispatchResult,
  setAnimationData,
}: FlyWalkDataDispatchProps): null {
  // Computes the project data that should be dispatched to this scene for rendering,
  // according to the user selection and the scene filter
  const dataDispatchResult = useFlyWalkDataDispatch(
    flyWalkSceneFilter,
    currentScene,
    currentArea,
  );

  const { target, animationComputer } = useInitialAnimationParams(
    dataDispatchResult.mainObject,
    isPointCloudObject(dataDispatchResult.overlayObject)
      ? null
      : dataDispatchResult.overlayObject,
    currentScene.panos,
    currentScene.activeSheetFor2DNavigation,
  );

  if (
    initialAnimationInfo.current &&
    !initialAnimationInfo.current.canSkipInitialTransition
  ) {
    initialAnimationInfo.current = {
      ...initialAnimationInfo.current,
      target,
      animationComputer,
    };
  }

  // Computes the current animation state and the previous main object for rendering.
  // Notably, the 'currentSceneFilter' below is different from the 'flyWalkSceneFilter' above.
  // The latter refers to what the user has selected in the UI. The former refers to what the
  // rendering pipeline is ready to take as input (as opposed to an animation)
  const animationData = useFlyWalkAnimation(
    flyWalkSceneFilter,
    dataDispatchResult.main,
    initialAnimationInfo,
    currentScene.activeSheetFor2DNavigation,
    defaultOrthoCamera,
  );

  useActivateZoomLens(
    dataDispatchResult.overlay,
    !!animationData.flyWalkAnimation,
    dataDispatchResult.annotations.isPano &&
      !dataDispatchResult.annotations.isPanoWithDepth,
    dataDispatchResult.annotations.isPanoPairedWithPickingPointCloud,
    !!dataDispatchResult.main &&
      isIElementGaussianSplats(dataDispatchResult.main),
  );

  useLayoutEffect(() => {
    setDataDispatchResult(dataDispatchResult);
  }, [dataDispatchResult, setDataDispatchResult]);

  useLayoutEffect(() => {
    setAnimationData(animationData);
  }, [animationData, setAnimationData]);

  return null;
}

export const INITIAL_DATA_DISPATCH_RESULT: DataDispatchResult = {
  main: null,
  mainObject: null,
  overlay: null,
  overlayObject: null,
  sheetObjects: [],
  annotations: {
    annotations: [],
    notCollapsibleAnnotations: [],
    isPano: false,
    isPanoWithValidPose: false,
    isPanoAligned: false,
    isPanoWithDepth: false,
    isPanoPairedWithPickingPointCloud: false,
    panoAdjustedPose: undefined,
  } satisfies AnnotationDispatch,
  measurements: [],
  imageType: ImageType.NoImage,
};
